import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getDomain, updateDomain } from '@/services/domainRegistry';
import PhaseApplicabilityField from '@/features/phases/components/PhaseApplicabilityField';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import {
  buildDomainActionDefinitions,
  buildDomainCapabilities,
  buildDomainSurfaces,
  capabilityIsEnabled,
  DOMAIN_ACTION_DEFINITION_CONTRACT,
} from '@/services/domain/centralModel';

const EMPTY_DEFINITION = {
  id: null,
  label: '',
  plural: '',
  description: '',
  active: true,
};

function DefinitionEditor({ value, busy, schoolId, domainApplicability, onChange, onCancel, onSave }) {
  return (
    <form
      className="rounded-2xl border border-base-300 bg-base-100 p-4 space-y-4"
      onSubmit={onSave}
    >
      <fieldset disabled={busy} className="min-w-0 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">
            {value.id ? 'Edit follow-up' : 'Add a follow-up'}
          </h3>
          <p className="mt-1 text-xs text-base-content/60">
            Rules can create this reusable kind of work in Queue.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square"
          onClick={onCancel}
          aria-label="Close follow-up editor"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="domain-action-label" className="text-sm font-medium">
            Singular name
          </label>
          <input
            id="domain-action-label"
            className="input input-bordered w-full"
            value={value.label}
            maxLength={DOMAIN_ACTION_DEFINITION_CONTRACT.maxLabelLength}
            onChange={(event) => onChange({ ...value, label: event.target.value })}
            placeholder="Collect signed form"
            required
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="domain-action-plural" className="text-sm font-medium">
            Plural name
          </label>
          <input
            id="domain-action-plural"
            className="input input-bordered w-full"
            value={value.plural}
            maxLength={DOMAIN_ACTION_DEFINITION_CONTRACT.maxLabelLength}
            onChange={(event) => onChange({ ...value, plural: event.target.value })}
            placeholder="Collect signed forms"
            required
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="domain-action-description" className="text-sm font-medium">
          Instructions
        </label>
        <textarea
          id="domain-action-description"
          className="textarea textarea-bordered min-h-24 w-full"
          value={value.description}
          maxLength={DOMAIN_ACTION_DEFINITION_CONTRACT.maxDescriptionLength}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
          placeholder="What should the person handling this work do?"
        />
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-base-200/60 p-3">
        <input
          type="checkbox"
          className="checkbox checkbox-primary checkbox-sm mt-0.5"
          checked={value.active}
          onChange={(event) => onChange({ ...value, active: event.target.checked })}
        />
        <span className="text-sm">
          <span className="block font-medium">Available to rules</span>
          <span className="mt-0.5 block text-base-content/60">
            Inactive definitions stay on the Domain for history but cannot be selected by a new rule.
          </span>
        </span>
      </label>

      <PhaseApplicabilityField schoolId={schoolId} value={value.phaseApplicability} domainApplicability={domainApplicability}
        disabled={busy} onChange={phaseApplicability => onChange({ ...value, phaseApplicability })} />
      <p className="text-xs text-base-content/65">This stays one shared definition. Phase settings take effect after phase processes are reviewed and activated.</p>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={busy || !value.label.trim() || !value.plural.trim()}
        >
          {busy && <span className="loading loading-spinner loading-xs" />}
          Save follow-up
        </button>
      </div>
      </fieldset>
    </form>
  );
}

function Status({ status, error }) {
  if (status === 'saving') {
    return <span className="badge badge-info badge-outline">Saving…</span>;
  }
  if (status === 'saved') {
    return (
      <span className="badge badge-success badge-outline gap-1">
        <CheckCircleIcon className="h-3.5 w-3.5" /> Saved
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="badge badge-error badge-outline gap-1" title={error || undefined}>
        <ExclamationCircleIcon className="h-3.5 w-3.5" /> Save failed
      </span>
    );
  }
  return null;
}

export default function DomainActionDefinitionsManager({ domain: liveDomain, schoolId }) {
  const [acknowledged, setAcknowledged] = useState(null);
  const domain = acknowledged && acknowledged.governance.configurationRevision >= (liveDomain.governance?.configurationRevision || 0)
    ? acknowledged : liveDomain;
  const editorRevision = useRef(null);
  const saving = useRef(false);
  const normalizedDefinitions = useMemo(
    () => buildDomainActionDefinitions(
      (domain?.actionDefinitions || []).filter((definition) => definition?.retired !== true),
    ),
    [domain?.actionDefinitions],
  );
  const [definitions, setDefinitions] = useState(normalizedDefinitions);
  const phaseView = usePhaseConfigurationView(domain);
  const visibleDefinitions = phaseView.filter(definitions);
  const [enabled, setEnabled] = useState(() => capabilityIsEnabled(domain, 'actions'));
  const [editor, setEditor] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    setDefinitions(normalizedDefinitions);
    setEnabled(capabilityIsEnabled(domain, 'actions'));
  }, [domain, normalizedDefinitions]);

  const openEditor = value => {
    editorRevision.current = domain.governance?.configurationRevision || 0;
    setEditor(value);
    setError(null); setStatus('idle');
  };

  const reload = async () => {
    if (saving.current) return;
    saving.current = true; setStatus('saving');
    try {
      const saved = await getDomain(schoolId, domain.id);
      if (!saved) throw new Error('This Domain is no longer available.');
      setAcknowledged(saved); setEditor(null); setError(null); setStatus('idle');
    } catch (failure) { setError(failure.message); setStatus('error'); }
    finally { saving.current = false; }
  };

  const persist = async (nextDefinitions, nextEnabled, expectedConfigurationRevision = domain.governance?.configurationRevision || 0) => {
    if (saving.current) return false;
    const capabilities = buildDomainCapabilities({
      ...(domain.capabilities || {}),
      actions: {
        ...(domain.capabilities?.actions || {}),
        enabled: nextEnabled,
        outcomes: nextEnabled ? ['queue-follow-up'] : [],
      },
      ...(!nextEnabled ? {
        rules: {
          ...(domain.capabilities?.rules || {}),
          enabled: false,
          categories: [],
        },
      } : {}),
    });
    saving.current = true;
    setStatus('saving');
    setError(null);
    try {
      const updates = {
        actionDefinitions: nextDefinitions,
        capabilities,
        ...(!nextEnabled ? {
          surfaces: buildDomainSurfaces({
            ...(domain.surfaces || {}),
            rules: { ...(domain.surfaces?.rules || {}), enabled: false },
          }),
        } : {}),
      };
      const result = await updateDomain(schoolId, domain.id, updates, { expectedConfigurationRevision });
      setAcknowledged({ ...domain, ...updates,
        governance: { ...domain.governance, configurationRevision: result.configurationRevision } });
      setDefinitions(nextDefinitions);
      setEnabled(nextEnabled);
      setStatus('saved');
      window.setTimeout(() => setStatus(current => current === 'saved' ? 'idle' : current), 2200);
      return true;
    } catch (saveError) {
      setError(['domain/configuration-conflict', 'functions/aborted'].includes(saveError.code)
        ? 'This Domain changed in another session. Your edits are still here. Reload saved settings, review them, then make your changes again.'
        : saveError.message || 'Could not save follow-ups');
      setStatus('error');
      throw saveError;
    } finally { saving.current = false; }
  };

  const save = async (event) => {
    event.preventDefault();
    const input = {
      ...editor,
      sortOrder: editor.id
        ? (definitions.find((definition) => definition.id === editor.id)?.sortOrder ?? 0)
        : definitions.length,
    };
    const candidate = editor.id
      ? definitions.map((definition) => (definition.id === editor.id ? input : definition))
      : [...definitions, input];
    let next;
    try {
      next = buildDomainActionDefinitions(candidate);
    } catch (saveError) {
      setError(saveError.message || 'Could not save follow-up');
      setStatus('error');
      return;
    }
    if (await persist(next, true, editorRevision.current).catch(() => false)) setEditor(null);
  };

  const toggleDefinition = async (definition) => {
    const next = buildDomainActionDefinitions(definitions.map((candidate) => (
      candidate.id === definition.id
        ? { ...candidate, active: candidate.active === false }
        : candidate
    )));
    const hasActive = next.some((candidate) => candidate.active !== false);
    await persist(next, enabled && hasActive).catch(() => {});
  };

  const toggleEnabled = async (nextEnabled) => {
    if (nextEnabled && !definitions.some((definition) => definition.active !== false)) {
      openEditor({ ...EMPTY_DEFINITION });
      setError('Add an available follow-up before enabling Queue work.');
      setStatus('error');
      return;
    }
    await persist(definitions, nextEnabled).catch(() => {});
  };

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-base-300 bg-base-100 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold">Queue follow-ups</h2>
              <Status status={status} error={error} />
            </div>
            <p className="mt-1 text-sm text-base-content/60">
              Define reusable work here. Rules create separate runtime records in Queue.
              Turning follow-ups off also pauses those automated rules.
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
            <span>{enabled ? 'Enabled' : 'Disabled'}</span>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={enabled}
              disabled={status === 'saving'}
              onChange={(event) => toggleEnabled(event.target.checked)}
              aria-label="Enable Queue follow-ups"
            />
          </label>
        </div>
        {error && status === 'error' && (
          <div className="alert alert-error mt-3 py-2 text-sm" role="alert">
            <ExclamationCircleIcon className="h-5 w-5" />
            <div>
              <p>{error}</p>
              <button type="button" className="btn btn-ghost btn-sm" onClick={reload}>
                Reload saved settings (discard edits)
              </button>
            </div>
          </div>
        )}
      </div>

      {editor ? (
        <DefinitionEditor
          value={editor}
          busy={status === 'saving'}
          schoolId={schoolId}
          domainApplicability={domain.phaseApplicability}
          onChange={setEditor}
          onCancel={() => setEditor(null)}
          onSave={save}
        />
      ) : (
        <>
          <div className="space-y-2">
            {visibleDefinitions.map((definition) => (
              <article
                key={definition.id}
                className="rounded-xl border border-base-300 bg-base-100 p-3"
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-square"
                    aria-label={definition.active === false ? `Activate ${definition.label}` : `Deactivate ${definition.label}`}
                    onClick={() => toggleDefinition(definition)}
                    disabled={status === 'saving'}
                  >
                    <CheckCircleIcon className={definition.active === false ? 'h-5 w-5 text-base-content/25' : 'h-5 w-5 text-success'} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={definition.active === false ? 'font-medium text-base-content/45 line-through' : 'font-medium'}>
                      {definition.label}
                    </p>
                    <p className="mt-0.5 text-xs text-base-content/55">{definition.plural}</p>
                    {definition.description && (
                      <p className="mt-2 text-sm text-base-content/65">{definition.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-square"
                    aria-label={`Edit ${definition.label}`}
                    onClick={() => openEditor({ ...definition })}
                    disabled={status === 'saving'}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
          {!phaseView.ready && <p role="status" className="text-sm text-base-content/65">Waiting for current phase settings…</p>}
          {phaseView.ready && definitions.length > 0 && visibleDefinitions.length === 0 &&
            <p className="text-sm text-base-content/65">No follow-ups in this phase view.</p>}
          {phaseView.ready && definitions.length === 0 && (
            <div className="rounded-2xl border border-dashed border-base-300 p-6 text-center">
              <p className="font-medium">No reusable follow-ups yet</p>
              <p className="mt-1 text-sm text-base-content/60">
                Event recording still works. Add a follow-up before enabling rules.
              </p>
            </div>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm gap-2"
            onClick={() => openEditor({ ...EMPTY_DEFINITION })}
            disabled={status === 'saving'}
          >
            <PlusIcon className="h-4 w-4" />
            Add follow-up
          </button>
        </>
      )}
    </section>
  );
}
